import { inject, Injectable, signal } from '@angular/core';
import { toObservable } from '@angular/core/rxjs-interop';
import { BehaviorSubject, catchError, map, Observable, switchMap, tap, throwError } from 'rxjs';
import { environment } from 'src/environments/environment';
import { HttpClientWrapperService } from '../http/http-client-wrapper.service';
import { GuestAuth, GuestCode, GuestSession, GuestUser } from '../../models/GuestUser';

@Injectable({
  providedIn: 'root',
})
export class GuestUserService {
  private readonly guestUserApiUrl = `${environment.beApiUrl}/api/v1/guest-users`;
  private readonly guestAuthApiUrl = `${environment.beApiUrl}/api/v1/guest-auth`;
  private httpClient = inject(HttpClientWrapperService);

  private readonly _currentGuestUser = signal<GuestUser | null>(null);
  public guestSession$: BehaviorSubject<string | null> = new BehaviorSubject<string | null>(null);

  readonly currentGuestUser = this._currentGuestUser.asReadonly();

  /** @deprecated Use AuthFacade.principal() — see PR #621 for migration plan. */
  readonly user$: Observable<GuestUser | null> = toObservable(this._currentGuestUser);

  constructor() {
    this.initializeAuth();
  }

  private initializeAuth() {
    const guestSessionId: string | null = localStorage.getItem('guestSessionId');
    const guestSessionExpiresAt: string | null = localStorage.getItem('guestSessionExpiresAt');
    const guestUserId: string | null = localStorage.getItem('guestUserId');

    if (!guestSessionId || !guestUserId || !guestSessionExpiresAt) {
      this.clearLocalGuestSession();
      return;
    }
  
    const expirationDate: Date = new Date(guestSessionExpiresAt);
    const now: Date = new Date();
  
    if (now > expirationDate) {
      this.endGuestUserSessions(guestUserId).subscribe({
        next: () => this.clearLocalGuestSession(),
        error: () => this.clearLocalGuestSession() 
      });
      return;
    }
  
    this.guestSession$.next(guestSessionId);
    this.getGuestUser(guestUserId).subscribe({
      next: (guestUser) => this._currentGuestUser.set(guestUser),
      error: (error) => {
        console.error(error);
        this.clearLocalGuestSession()
      }
    });
  }

  createGuestUser(guestUser: GuestUser): Observable<GuestUser> {
    return this.httpClient.post<GuestUser, GuestUser>(`${this.guestUserApiUrl}`, guestUser);
  }

  verifyUserCode(code: GuestCode, id: string): Observable<GuestUser> {
    return this.httpClient.post<GuestUser, GuestCode>(`${this.guestUserApiUrl}/${id}/verify`, code);
  }

  sendVerification(phoneNumber: string): Observable<null> {
    return this.httpClient.post(`${this.guestUserApiUrl}/send-verification/${phoneNumber}`, null);
  }

  startGuestUserSession(guestUserAuth: GuestAuth): Observable<GuestSession> {
    return this.httpClient.post<GuestSession, GuestAuth>(`${this.guestAuthApiUrl}`, guestUserAuth)
    .pipe(
       tap((session: GuestSession) => {
        localStorage.setItem('guestSessionId', session.sessionId);
        localStorage.setItem('guestUserId', session.guestUserId.toString());
        localStorage.setItem('guestSessionExpiresAt', session.expiresAt); 
        this.guestSession$.next(session.sessionId);
       }),
       switchMap((session: GuestSession) => 
        this.getGuestUser(session.guestUserId.toString()).pipe(
          tap((guestUser: GuestUser) => {
            this._currentGuestUser.set(guestUser);
          }),
          map(() => session)
        )
      ),
      catchError((error) => {
        console.error('guest session error:', error);
        return throwError(() => new Error('Login failed. Please try again.'));
      })
    );
  }

  getGuestUser(id: string): Observable<GuestUser> {
    return this.httpClient.getGuest<GuestUser>(`${this.guestUserApiUrl}/${id}`);
  }

  deleteGuestUser(id: string): Observable<void> {
    return this.httpClient.deleteGuest<void>(`${this.guestUserApiUrl}/${id}`)
    .pipe(
      tap(() =>{
        this.clearLocalGuestSession();
      })
    )
  }

  endGuestUserSessions(id: string): Observable<void> {
    const guestUserAuth: GuestAuth = {
      guestUserId: Number(id)
    }
    return this.httpClient.postGuest<void, GuestAuth>(`${this.guestAuthApiUrl}/logout`, guestUserAuth)
    .pipe(
      tap(() =>{
        this.clearLocalGuestSession();
      })
    )
  }

  public clearLocalGuestSession(): void {
    localStorage.removeItem('guestUserId');
    localStorage.removeItem('guestSessionId');
    localStorage.removeItem('guestSessionExpiresAt');
    
    this._currentGuestUser.set(null);
    this.guestSession$.next(null);
  }

  getGuestUserByPhoneNumber(number: string): Observable<GuestUser> {
    return this.httpClient.get<GuestUser>(`${this.guestUserApiUrl}/phone/${number}`);
  }
}
